import Str from 'utils/Str';
import Base64 from 'utils/Base64';
import generalInterface from './generalInterface';
import { getAccessToken } from './helper.storage';
import axios from 'axios';
import ImageHelper from 'utils/ImageHelper';
import { ServiceConfig } from 'constants/shared/ServiceConfig';
import Config from 'config/Config';
import interfaceBridge from 'utils/interfaceBridge';
import { allowedExtensions } from 'constants/frontend/app/allowedExtensions';
import { getTopLeftImage, getTopLeftResizedFile } from './helper.image';

console.log('helper.uploader.js loaded');

const EVENT_TYPE_UPLOAD_SINGLE = 'upload_single';

const STARTED = 'started';
const UPLOADING = 'uploading';
const COMPLETE = 'complete';
const ERROR = 'error';

const SINGLE_IMAGE_FIELD = 'myImage';
const SINGLE_ATTACHMENT_FIELD = 'myAttachment';
const SINGLE_TEMPFILE_FIELD = 'myTempFile';

const dataURItoBlob = (dataURI: string) => {
  const bytes =
    dataURI.split(',')[0].indexOf('base64') >= 0
      ? atob(dataURI.split(',')[1])
      : unescape(dataURI.split(',')[1]);
  const mime = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const max = bytes.length;
  const ia = new Uint8Array(max);
  for (let i = 0; i < max; i++) ia[i] = bytes.charCodeAt(i);
  return new Blob([ia], { type: mime });
};

const getDataUrlFromImage = (image: HTMLImageElement, mimeType: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return null;

  canvas.width = image.width;
  canvas.height = image.height;
  context.drawImage(image, 0, 0);

  if (mimeType === 'image/png') {
    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl;
  }
  const dataUrl = canvas.toDataURL('image/jpeg', Config.imageUpload.jpegQuality);
  return dataUrl;
};

const waitForImageLoad = async (image: HTMLImageElement) => {
  if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
    return true;
  }

  return new Promise<boolean>((resolve) => {
    const handleLoad = () => {
      cleanup();
      resolve(image.naturalWidth > 0 && image.naturalHeight > 0);
    };
    const handleError = () => {
      cleanup();
      resolve(false);
    };
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      image.removeEventListener('load', handleLoad);
      image.removeEventListener('error', handleError);
    };

    const timeoutId = window.setTimeout(handleError, 5000);
    image.addEventListener('load', handleLoad);
    image.addEventListener('error', handleError);

    // 이미지가 위의 complete 확인 직후 로드된 경우도 처리한다.
    if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
      handleLoad();
    }
  });
};

const resolveImageMimeType = (mimeType: string) => {
  return mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
};

const sendNativeEvent = (eventType, eventJsonStr) => {
  const encodedEvent = Base64.encode(eventJsonStr);
  generalInterface.onNativeEvent(eventType, encodedEvent);
};

const getFileExtensionByFileName = (fileName) => {
  const lastIndexOfDot = fileName.lastIndexOf('.');

  // Check if the dot is in the last position of the string or no dot is found
  if (lastIndexOfDot === -1 || lastIndexOfDot === fileName.length - 1) {
    return '';
  }

  // Extract and return the extension from the last dot to the end of the string
  return fileName.substring(lastIndexOfDot).toLowerCase();
};

const uploadFile = async (
  academyId: number,
  contentIdentifier: string,
  kind: 'contentImage' | 'contentAttachment' | 'tempImage',
  fileObj: File,
  eventIdentifier: string,
  tempFileType?: string, // temp 파일의 body.type,  접두어로 사용됨, 나중에 서버쪽에서 api를 호출해 정식 폴더로 옮김
) => {
  const fileKey = Str.randomAlphaNumericStr(10);
  console.log('fileObj', fileObj);
  const data = new FormData();
  let fileForUpload = fileObj;
  let url = '';

  if (kind === 'contentImage') {
    const contentImageMimeType = resolveImageMimeType(fileObj.type);
    const transformedFile = await getTopLeftResizedFile(
      fileObj,
      ServiceConfig.Post.imageMaxLongSide,
      contentImageMimeType,
    );
    fileForUpload = transformedFile || fileObj;

    const imageInfo = await ImageHelper.getImageInfoFromFile(fileForUpload);

    if (!imageInfo) {
      // todo 실패 처리
      return;
    }

    const { width, height, base64ThumbnailData } = imageInfo;

    console.log('width=', width);
    console.log('height=', height);
    console.log('base64Data=', base64ThumbnailData);
    sendNativeEvent(
      EVENT_TYPE_UPLOAD_SINGLE,
      JSON.stringify({
        type: 'image',
        fileKey,
        uploadStatus: STARTED,
        fileName: fileForUpload.name,
        mimeType: fileForUpload.type,
        bytesUploaded: 0,
        contentLength: fileForUpload.size,
        imageWidth: width,
        imageHeight: height,
        base64Data: base64ThumbnailData,
        contentIdentifier,
      }),
    );
    url = `${Config.fileServerBaseUrl}/${kind}`;
    data.append('academyId', academyId.toString());
    data.append(SINGLE_IMAGE_FIELD, fileForUpload);
    //
  } else if (kind === 'contentAttachment') {
    sendNativeEvent(
      EVENT_TYPE_UPLOAD_SINGLE,
      JSON.stringify({
        type: 'attachment',
        fileKey,
        uploadStatus: STARTED,
        fileName: fileForUpload.name,
        mimeType: fileForUpload.type,
        bytesUploaded: 0,
        contentLength: fileForUpload.size,
        contentIdentifier,
      }),
    );

    if (fileForUpload.size > ServiceConfig.Post.attachmentMaxSizeByte) {
      const maxSizeMB = Math.floor(
        ServiceConfig.Post.attachmentMaxSizeByte / (1024 * 1024),
      );
      const failEvent = {
        fileKey,
        uploadStatus: ERROR,
        errorMessage: `첨부파일은 최대 ${maxSizeMB}MB까지 업로드할 수 있습니다.`,
        fileName: fileForUpload.name,
        mimeType: fileForUpload.type,
        bytesUploaded: fileForUpload.size,
        contentLength: fileForUpload.size,
        path: '',
        contentIdentifier,
      };
      sendNativeEvent(EVENT_TYPE_UPLOAD_SINGLE, JSON.stringify(failEvent));
      return;
    }

    // extension 체크
    const extension = getFileExtensionByFileName(fileForUpload.name);
    console.log('extension', extension);
    if (!allowedExtensions.includes(extension)) {
      const failEvent = {
        fileKey,
        uploadStatus: ERROR,
        errorMessage: 'invalid mimeType',
        fileName: fileForUpload.name,
        mimeType: fileForUpload.type,
        bytesUploaded: fileForUpload.size,
        contentLength: fileForUpload.size,
        path: '',
        contentIdentifier,
      };
      sendNativeEvent(EVENT_TYPE_UPLOAD_SINGLE, JSON.stringify(failEvent));
      return;
    }

    url = `${Config.fileServerBaseUrl}/${kind}`;
    data.append('academyId', academyId.toString());
    data.append(SINGLE_ATTACHMENT_FIELD, fileForUpload);
    //
  } else if (kind === 'tempImage') {
    //
    // inapp 에서 uploadTempImage는 fileUploader의 uploadImage를 이용한다.
    url = `${Config.fileServerBaseUrl}/tempFile`;
    data.append('academyId', academyId.toString());
    data.append(SINGLE_TEMPFILE_FIELD, fileForUpload);
    // tempFile 은 type을 필요로 한다.
    // 어떤 작업을 위한 임시파일인지
    if (!tempFileType) {
      throw new Error('tempFile must come with type');
    }
    data.append('type', tempFileType);
  }

  const onUploadProgress = (progressEvent) => {
    sendNativeEvent(
      EVENT_TYPE_UPLOAD_SINGLE,
      JSON.stringify({
        fileKey,
        uploadStatus: UPLOADING,
        fileName: fileForUpload.name,
        mimeType: fileForUpload.type,
        bytesUploaded: progressEvent.loaded,
        contentLength: fileForUpload.size,
        contentIdentifier,
      }),
    );
  };
  const accessToken = getAccessToken();
  const config = {
    onUploadProgress,
    headers: { authorization: `Bearer ${accessToken}` },
  };
  axios
    .post(url, data, config)
    .then((res) => {
      console.log('upload end, response', res.data);
      const uploadEvent = {
        fileKey,
        uploadStatus: COMPLETE,
        fileName: fileForUpload.name,
        mimeType: fileForUpload.type,
        bytesUploaded: fileForUpload.size,
        contentLength: fileForUpload.size,
        path: res.data.filePath,
        contentIdentifier,
      };
      sendNativeEvent(EVENT_TYPE_UPLOAD_SINGLE, JSON.stringify(uploadEvent));

      // 단일 파일 요청 등 eventIdentifier가 주어지고
      // 성공, 실패 등 최종 이벤트일 때는 결과를 넣어줌
      if (eventIdentifier) {
        console.log('eventIdentifier = ', eventIdentifier);
        generalInterface.requestProcessed(
          eventIdentifier,
          Base64.encode(JSON.stringify(uploadEvent)),
        );
      }
    })
    .catch((error) => {
      console.error('Upload failed with error:', error);
      let errorMessage = error.message;
      // Optionally handle specific HTTP status codes
      if (error.response && error.response.status === 400) {
        console.error('Bad Request:', error.response.data);
        errorMessage = error.response.data;
        // Add specific logic here for a 400 error, e.g., notifying the user
      } else {
        // Handle other errors
        console.error('An error occurred:', error.message);
      }

      // Emit a fail event or any other cleanup
      const failEvent = {
        fileKey,
        uploadStatus: ERROR,
        errorMessage,
        fileName: fileForUpload.name,
        mimeType: fileForUpload.type,
        bytesUploaded: fileForUpload.size,
        contentLength: fileForUpload.size,
        path: '',
        contentIdentifier,
      };
      sendNativeEvent(EVENT_TYPE_UPLOAD_SINGLE, JSON.stringify(failEvent));
    });
};

const uploadContentImage = (messageObj, _sender) => {
  if (messageObj.action !== 'uploadContentImage') {
    return { handled: false };
  }

  const uploadElem = document.createElement('input');
  uploadElem.setAttribute('type', 'file');
  uploadElem.setAttribute('accept', 'image/jpeg,image/png');
  uploadElem.setAttribute('multiple', 'true');
  uploadElem.click();

  uploadElem.addEventListener('change', function () {
    if (uploadElem.files && uploadElem.files.length) {
      const files = [...uploadElem.files];

      if (files.length > messageObj.maxCount) {
        alert(`${messageObj.maxCount}개의 이미지만 업로드합니다.`);
        files.splice(messageObj.maxCount);
      }

      console.log('uploadElem.files', files);
      for (let i = 0; i < files.length; ++i) {
        uploadFile(
          messageObj.academyId,
          messageObj.contentIdentifier,
          'contentImage',
          files[i],
          '', // tempFileType 은 kind === 'tempImage' 일때만 필요
        );
      }
    }
  });

  return {
    handled: true,
  };
};

const uploadContentAttachment = (messageObj, _sender) => {
  if (messageObj.action !== 'uploadContentAttachment') {
    return { handled: false };
  }

  console.log('uploadContentAttachment helper');

  const uploadElem = document.createElement('input');
  uploadElem.setAttribute('type', 'file');
  uploadElem.setAttribute('accept', '*/*');
  uploadElem.setAttribute('multiple', 'false'); // 첨부파일은 하나씩
  uploadElem.click();

  uploadElem.addEventListener('change', function () {
    if (uploadElem.files && uploadElem.files.length) {
      const files = [...uploadElem.files];

      if (files.length > messageObj.maxCount) {
        alert(`${messageObj.maxCount}개의 파일만 업로드합니다.`);
        files.splice(messageObj.maxCount);
      }

      for (let i = 0; i < uploadElem.files.length; ++i) {
        uploadFile(
          messageObj.academyId,
          messageObj.contentIdentifier,
          'contentAttachment',
          files[i],
          '', // tempFileType 은 kind === 'tempImage' 일때만 필요
        );
      }
    }
  });

  return {
    handled: true,
  };
};

const uploadTempImage = (messageObj, _sender) => {
  if (messageObj.action !== 'uploadTempImage') {
    return { handled: false };
  }

  /*
  messageObj 구성
      academyId,
      type,
      maxWidth,
      ratioW,
      ratioH,
  */

  const uploadElem = document.createElement('input');
  uploadElem.type = 'file';
  uploadElem.accept = 'image/jpeg,image/png';
  uploadElem.multiple = false;
  uploadElem.click();

  uploadElem.addEventListener('change', async function () {
    if (uploadElem.files && uploadElem.files.length) {
      const file = uploadElem.files[0];
      let image = await getTopLeftImage(file);

      if (!image) {
        await interfaceBridge.customAlert(
          '이미지 읽기 실패',
          '이미지를 읽을 수 없습니다.',
          '닫기',
        );
        return;
      }

      console.log('uploadTempImage', messageObj);

      // 프로필 이미지 요청인 경우 crop 진행
      if (
        messageObj.type === 'userProfile' ||
        messageObj.type === 'studentProfile'
      ) {
        image = ImageHelper.cropCenter(
          image,
          ServiceConfig.User.maxProfileImageSize,
          1,
          1,
        );
      } else if (
        messageObj.ratioW === 0 &&
        messageObj.ratioH === 0 &&
        messageObj.maxWidth // 비율이 없을 때 maxWidth는 maxLongSide의 역할을 한다.
      ) {
        image = ImageHelper.resize(image, messageObj.maxWidth);
      }

      if (!image) {
        await interfaceBridge.customAlert(
          '이미지 변환 실패',
          '이미지의 크기를 조절하는데 실패했습니다.',
          '닫기',
        );
        return;
      }

      // cropCenter/resize는 canvas data URL을 src로 가진 새 이미지를 반환한다.
      // 디코딩 전에 canvas에 그리면 0x0 data URL에서 MIME type이 빈 파일이 만들어질 수 있다.
      if (!(await waitForImageLoad(image))) {
        await interfaceBridge.customAlert(
          '이미지 변환 실패',
          '이미지 데이터를 변환하는데 실패했습니다.',
          '닫기',
        );
        return;
      }

      // resize하면서 toDataUrl 로 넣었음
      const mimeType = resolveImageMimeType(file.type);
      const dataUrl = getDataUrlFromImage(image, mimeType);
      if (!dataUrl) {
        await interfaceBridge.customAlert(
          '이미지 변환 실패',
          '이미지 데이터 변환에 실패했습니다.',
          '닫기',
        );
        return;
      }
      const blob = dataURItoBlob(dataUrl);
      const ext = blob.type === 'image/jpeg' ? 'jpg' : 'png';
      console.log('image ext = ', ext);
      const fileToUpload = new File([blob], `image.${ext}`, {
        type: blob.type,
      });

      uploadFile(
        messageObj.academyId,
        'dummy',
        'tempImage',
        fileToUpload,
        messageObj.identifier,
        messageObj.type, // temp 파일의 body.type,  접두어로 사용됨, 나중에 서버쪽에서 api를 호출해 정식 폴더로 옮김
      );
    }
  });

  return {
    handled: true,
  };
};

export { uploadContentImage, uploadContentAttachment, uploadTempImage };
